import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  SimpleGrid,
  VStack,
  useToast,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Divider,
  Text,
  Badge,
  Box,
  Image,
  useDisclosure,
  Icon,
} from '@chakra-ui/react';
import { FaPrint } from 'react-icons/fa';
import { useState, useEffect } from 'react';
import MapSelector from '../../../components/MapSelector';

const API_BASE_URL = `${process.env.REACT_APP_API_URL || "http://127.0.0.1:5000"}`;

interface LicenciaVinosLicores {
  Id?: number;
  usuarioId?: number;
  nombreUsuario?: string;
  ciudadano?: string;
  nombreEstablecimiento?: string;
  giroVinosLicoresId?: number;
  tipo?: string;
  estatus?: string;
  movimiento?: string;
  horario?: string;
  direccion?: string;
  localidad?: string;
  calle?: string;
  numero?: string;
  codigoPostal?: string;
  lat?: number;
  lng?: number;
  folioLicencia?: string;
  montoLicencia?: number;
  fechaRegistro?: string;
  referenciaPago?: string;
  fechaPago?: string;
  pagado?: boolean;
  creadoEn?: string;
  actualizadoEn?: string;
  descripcion?: string;
  giroVinosLicores?: {
    codigo: string;
    descripcion: string;
  };
  archivosINE?: Array<{
    id: number;
    url: string;
    tipo: string;
    descripcion: string;
  }>;
  comprobantesPago?: Array<{
    id: number;
    url: string;
    tipo: string;
    descripcion: string;
  }>;
}

interface Giro {
  id: number;
  descripcion: string;
  activo: boolean;
}

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  licencia: LicenciaVinosLicores | null;
  modo: 'view' | 'edit' | 'create';
  onSave: () => void;
}

const ModalLicenciaVinosLicores = ({ isOpen, onClose, licencia, modo, onSave }: ModalProps) => {
  const [formData, setFormData] = useState<LicenciaVinosLicores>({
    usuarioId: 1,
    nombreUsuario: '',
    ciudadano: '',
    nombreEstablecimiento: '',
    giroVinosLicoresId: 0,
    tipo: 'Temporal',
    estatus: 'Pendiente',
    movimiento: '',
    horario: '',
    direccion: '',
    localidad: '',
    calle: '',
    numero: '',
    codigoPostal: '',
    lat: 19.325665,
    lng: -98.256841,
    montoLicencia: 0,
    fechaRegistro: '',
    referenciaPago: '',
    fechaPago: '',
    pagado: false,
    descripcion: '',
  });

  const [giros, setGiros] = useState<Giro[]>([]);
  const [loading, setLoading] = useState(false);
  const [archivosINE, setArchivosINE] = useState<File[]>([]);
  const [comprobantesPago, setComprobantesPago] = useState<File[]>([]);
  const [imagenSeleccionada, setImagenSeleccionada] = useState<string>('');
  const { isOpen: isImageModalOpen, onOpen: onImageModalOpen, onClose: onImageModalClose } = useDisclosure();
  const toast = useToast();

  useEffect(() => {
    const fetchGiros = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/licencias-vinos-licores/catalogos/giros`);
        const data = await res.json();
        setGiros(data);
      } catch (error) {
        console.error('Error al cargar giros:', error);
      }
    };

    if (isOpen) {
      fetchGiros();
    }
  }, [isOpen]);

  useEffect(() => {
    if (licencia && (modo === 'view' || modo === 'edit')) {
      setFormData({
        ...licencia,
        fechaRegistro: licencia.fechaRegistro ? licencia.fechaRegistro.split('T')[0] : '',
        fechaPago: licencia.fechaPago ? licencia.fechaPago.split('T')[0] : '',
      });
    } else if (modo === 'create') {
      setFormData({
        usuarioId: 1,
        nombreUsuario: '',
        ciudadano: '',
        nombreEstablecimiento: '',
        giroVinosLicoresId: 0,
        tipo: 'Temporal',
        estatus: 'Pendiente',
        movimiento: '',
        horario: '',
        direccion: '',
        localidad: '',
        calle: '',
        numero: '',
        codigoPostal: '',
        lat: 19.325665,
        lng: -98.256841,
        montoLicencia: 0,
        fechaRegistro: '',
        referenciaPago: '',
        fechaPago: '',
        pagado: false,
        descripcion: '',
      });
      setArchivosINE([]);
      setComprobantesPago([]);
    }
  }, [licencia, modo]);

  const handleInputChange = (field: keyof LicenciaVinosLicores, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileChange = (files: FileList | null, tipo: 'ine' | 'comprobante') => {
    if (files) {
      const fileArray = Array.from(files);
      if (tipo === 'ine') {
        setArchivosINE(fileArray.slice(0, 2));
      } else {
        setComprobantesPago(fileArray.slice(0, 2));
      }
    }
  };

  const handleArchivoClick = (url: string, tipo: string) => {
    const fullUrl = `${process.env.REACT_APP_API_URL || "http://127.0.0.1:5000"}/uploads/${url.split('uploads\\')[1]?.replace(/\\/g, '/')}`;
    if (tipo === 'pdf') {
      window.open(fullUrl, '_blank');
    } else {
      setImagenSeleccionada(fullUrl);
      onImageModalOpen();
    }
  };

  const deleteArchivo = async (archivoId: number, tipo: 'ine' | 'comprobante') => {
    try {
      const endpoint = tipo === 'ine' ? 'ine' : 'comprobantes';
      const response = await fetch(`${API_BASE_URL}/api/licencias-vinos-licores/${endpoint}/${archivoId}`, {
        method: 'DELETE',
      });
      
      if (response.ok) {
        // Actualizar formData eliminando el archivo
        if (tipo === 'ine') {
          setFormData(prev => ({
            ...prev,
            archivosINE: prev.archivosINE?.filter(archivo => archivo.id !== archivoId)
          }));
        } else {
          setFormData(prev => ({
            ...prev,
            comprobantesPago: prev.comprobantesPago?.filter(comprobante => comprobante.id !== archivoId)
          }));
        }
        
        toast({
          title: 'Éxito',
          description: 'Archivo eliminado correctamente',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        throw new Error('Error al eliminar archivo');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Error al eliminar el archivo',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const uploadArchivos = async (licenciaId: number) => {
    try {
      if (archivosINE.length > 0) {
        const formDataINE = new FormData();
        archivosINE.forEach(file => {
          formDataINE.append('archivos', file);
        });
        
        const responseINE = await fetch(`${API_BASE_URL}/api/licencias-vinos-licores/${licenciaId}/ine`, {
          method: 'POST',
          body: formDataINE,
        });
        
        if (!responseINE.ok) {
          throw new Error('Error al subir archivos INE');
        }
      }
      
      if (comprobantesPago.length > 0) {
        const formDataComprobantes = new FormData();
        comprobantesPago.forEach(file => {
          formDataComprobantes.append('archivos', file);
        });
        
        const responseComprobantes = await fetch(`${API_BASE_URL}/api/licencias-vinos-licores/${licenciaId}/comprobantes`, {
          method: 'POST',
          body: formDataComprobantes,
        });
        
        if (!responseComprobantes.ok) {
          throw new Error('Error al subir comprobantes de pago');
        }
      }
    } catch (error) {
      console.error('Error uploading files:', error);
      throw error;
    }
  };

  const imprimirLicencia = (lic: LicenciaVinosLicores) => {
    const fecha = lic.creadoEn?.split('T')[0] ?? '';
    const [anio, mes, dia] = fecha.split('-');

    const html = `
      <html>
        <head>
          <title>Licencia de Vinos y Licores</title>
          <style>
            @media print {
              body {
                margin: 0;
                padding: 0;
              }
            }
            body {
              position: relative;
              width: 21cm;
              height: 27.9cm;
              margin: 0;
              font-family: Arial, sans-serif;
              font-size: 12pt;
            }
            .campo {
              position: absolute;
              text-transform: uppercase;
            }
            #giro { top: 130px; left: 1px; }
            #tipo { top: 210px; left: 1px; }
            #anio { top: 300px; left: 1px; }
            #ciudadano { top: 120px; left: 470px; }
            #nombreEstablecimiento { top: 200px; left: 550px; }
            #direccion { top: 240px; left: 480px; }
            #horario { top: 310px; left: 470px; }
            #fecha1 { top: 365px; left: 280px; }
            #fecha2 { top: 365px; left: 400px; }
            #fecha3 { top: 365px; left: 600px; }
            #folio { top: 80px; left: 600px; }
            #estatus { top: 160px; left: 50px; }
          </style>
        </head>
        <body>
          <div id="giro" class="campo">${lic.giroVinosLicores?.descripcion ?? ''}</div>
          <div id="tipo" class="campo">${lic.tipo ?? 'Temporal'}</div>
          <div id="anio" class="campo">${anio ?? ''}</div>
          <div id="ciudadano" class="campo">${lic.ciudadano ?? ''}</div>
          <div id="nombreEstablecimiento" class="campo">${lic.nombreEstablecimiento ?? ''}</div>
          <div id="direccion" class="campo">${lic.direccion ?? ''}</div>
          <div id="horario" class="campo">${lic.horario ?? '9:00 am - 10:00 pm'}</div>
          <div id="fecha1" class="campo">${dia}</div>
          <div id="fecha2" class="campo">${mes}</div>
          <div id="fecha3" class="campo">${anio}</div>
          <div id="folio" class="campo">${lic.folioLicencia ?? lic.Id}</div>
          <div id="estatus" class="campo">${lic.estatus ?? 'Pendiente'}</div>

          <script>
            window.onload = function () {
              window.print();
            };
          </script>
        </body>
      </html>
    `;

    const ventana = window.open('', '_blank', 'width=800,height=600');
    if (ventana) {
      ventana.document.write(html);
      ventana.document.close();
    }
  };

  const handleSave = async () => {
    if (!formData.ciudadano || !formData.giroVinosLicoresId || !formData.direccion) {
      toast({
        title: 'Error',
        description: 'Por favor completa los campos obligatorios: Ciudadano, Giro y Dirección',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    try {
      const url = modo === 'create' 
        ? `${API_BASE_URL}/api/licencias-vinos-licores`
        : `${API_BASE_URL}/api/licencias-vinos-licores/${formData.Id}`;
      
      const method = modo === 'create' ? 'POST' : 'PUT';
      
      const dataToSend = {
        ...formData,
        lat: formData.lat ? Number(formData.lat) : null,
        lng: formData.lng ? Number(formData.lng) : null
      };
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(dataToSend),
      });

      if (response.ok) {
        const licenciaCreada = await response.json();
        
        if ((modo === 'create' || modo === 'edit') && (archivosINE.length > 0 || comprobantesPago.length > 0)) {
          await uploadArchivos(modo === 'create' ? licenciaCreada.Id : formData.Id!);
        }
        
        toast({
          title: 'Éxito',
          description: `Licencia ${modo === 'create' ? 'creada' : 'actualizada'} correctamente`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        onSave();
        onClose();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error en la respuesta del servidor');
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || `Error al ${modo === 'create' ? 'crear' : 'actualizar'} la licencia`,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const getEstatusColor = (estatus: string) => {
    switch (estatus) {
      case 'Activa': return 'green';
      case 'Pendiente': return 'orange';
      case 'Suspendida': return 'red';
      case 'Vencida': return 'gray';
      default: return 'blue';
    }
  };

  const isReadOnly = modo === 'view';

  return (
    <>
    <Modal isOpen={isOpen} onClose={onClose} size="4xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader
          bgImage="url('/img/bg.webp')"
          bgSize="cover"
          bgPosition="center"
          bgRepeat="no-repeat"
          color="white"
          textShadow="2px 2px 4px rgba(0,0,0,0.7)"
          fontWeight="bold"
          position="relative"
        >
          {modo === 'create' && 'Nueva Licencia de Vinos y Licores'}
          {modo === 'edit' && 'Editar Licencia de Vinos y Licores'}
          {modo === 'view' && 'Detalles de Licencia de Vinos y Licores'}
        </ModalHeader>
        <ModalCloseButton color="white" _hover={{ bg: 'rgba(255,255,255,0.2)' }} />
        
        <ModalBody>
          
          <VStack spacing={4} align="stretch">
            {modo === 'view' && formData.folioLicencia && (
              <Box p={3} bg="gray.50" borderRadius="md">
                <Text fontSize="sm" color="gray.600">Folio: <strong>{formData.folioLicencia}</strong></Text>
                <Text fontSize="sm" color="gray.600">
                  Estatus: <Badge colorScheme={getEstatusColor(formData.estatus || 'Pendiente')}>{formData.estatus || 'Pendiente'}</Badge>
                </Text>
              </Box>
            )}
            <Text fontSize="lg" fontWeight="bold" color="#5b182b">Datos del Ciudadano</Text>

            {/* Datos Básicos */}
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>

              <FormControl isRequired>
                <FormLabel>Ciudadano</FormLabel>
                <Input
                  value={formData.ciudadano || ''}
                  onChange={(e) => handleInputChange('ciudadano', e.target.value)}
                  isReadOnly={isReadOnly}
                />
              </FormControl>
              
              <FormControl>
                <FormLabel>Nombre del Establecimiento</FormLabel>
                <Input
                  value={formData.nombreEstablecimiento || ''}
                  onChange={(e) => handleInputChange('nombreEstablecimiento', e.target.value)}
                  isReadOnly={isReadOnly}
                />
              </FormControl>
              {modo !== 'view' && (
                <FormControl>
                      <FormLabel>Archivos INE (Máximo 2)</FormLabel>
                      <Input
                        type="file"
                        multiple
                        accept="image/*,.pdf"
                        onChange={(e) => handleFileChange(e.target.files, 'ine')}
                      />
                      {archivosINE.length > 0 && (
                        <Text fontSize="sm" color="blue.600" mt={1}>
                          {archivosINE.length} archivo(s) seleccionado(s)
                        </Text>
                      )}
                    </FormControl>
              )}
              
              <FormControl isRequired>
                <FormLabel>Giro</FormLabel>
                <Select
                  value={formData.giroVinosLicoresId || 0}
                  onChange={(e) => handleInputChange('giroVinosLicoresId', parseInt(e.target.value))}
                  isDisabled={isReadOnly}
                >
                  <option value={0}>Seleccionar giro</option>
                  {giros.map((giro) => (
                    <option key={giro.id} value={giro.id}>
                      {giro.descripcion}
                    </option>
                  ))}
                </Select>
              </FormControl>
              
              <FormControl>
                <FormLabel>Tipo</FormLabel>
                <Select
                  value={formData.tipo || 'Temporal'}
                  onChange={(e) => handleInputChange('tipo', e.target.value)}
                  isDisabled={isReadOnly}
                >
                  <option value="Temporal">Temporal</option>
                  <option value="Anual">Anual</option>
                </Select>
              </FormControl>
              
              <FormControl>
                <FormLabel>Estatus</FormLabel>
                <Select
                  value={formData.estatus || 'Pendiente'}
                  onChange={(e) => handleInputChange('estatus', e.target.value)}
                  isDisabled={isReadOnly}
                >
                  <option value="Pendiente">Pendiente</option>
                  <option value="Activa">Activa</option>
                  <option value="Suspendida">Suspendida</option>
                  <option value="Vencida">Vencida</option>
                </Select>
              </FormControl>
              
              <FormControl>
                <FormLabel>Movimiento</FormLabel>
                <Input
                  value={formData.movimiento || ''}
                  onChange={(e) => handleInputChange('movimiento', e.target.value)}
                  isReadOnly={isReadOnly}
                />
              </FormControl>
              
              <FormControl>
                <FormLabel>Horario</FormLabel>
                <Input
                  value={formData.horario || ''}
                  onChange={(e) => handleInputChange('horario', e.target.value)}
                  placeholder="Ej: 10:00 - 22:00"
                  isReadOnly={isReadOnly}
                />
              </FormControl>
            </SimpleGrid>

            <Divider />

            {/* Ubicación con Mapa */}
            <Text fontSize="lg" fontWeight="bold" color="#5b182b">Ubicación</Text>
            
            {modo !== 'view' && (
              <MapSelector
                onLocationSelect={(lat, lng, address) => {
                  handleInputChange('lat', lat);
                  handleInputChange('lng', lng);
                  if (address.street) handleInputChange('calle', address.street);
                  if (address.number) handleInputChange('numero', address.number);
                  if (address.city) handleInputChange('localidad', address.city);
                  if (address.postalCode) handleInputChange('codigoPostal', address.postalCode);
                  if (address.fullAddress) handleInputChange('direccion', address.fullAddress);
                }}
                initialLat={formData.lat}
                initialLng={formData.lng}
              />
            )}
            
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
              <FormControl isRequired>
                <FormLabel>Dirección</FormLabel>
                <Input
                  value={formData.direccion || ''}
                  onChange={(e) => handleInputChange('direccion', e.target.value)}
                  isReadOnly={isReadOnly}
                />
              </FormControl>
              
              <FormControl>
                <FormLabel>Localidad</FormLabel>
                <Input
                  value={formData.localidad || ''}
                  onChange={(e) => handleInputChange('localidad', e.target.value)}
                  isReadOnly={isReadOnly}
                />
              </FormControl>
              
              <FormControl>
                <FormLabel>Calle</FormLabel>
                <Input
                  value={formData.calle || ''}
                  onChange={(e) => handleInputChange('calle', e.target.value)}
                  isReadOnly={isReadOnly}
                />
              </FormControl>
              
              <FormControl>
                <FormLabel>Número</FormLabel>
                <Input
                  value={formData.numero || ''}
                  onChange={(e) => handleInputChange('numero', e.target.value)}
                  isReadOnly={isReadOnly}
                />
              </FormControl>
              
              <FormControl>
                <FormLabel>Código Postal</FormLabel>
                <Input
                  value={formData.codigoPostal || ''}
                  onChange={(e) => handleInputChange('codigoPostal', e.target.value)}
                  isReadOnly={isReadOnly}
                />
              </FormControl>
            </SimpleGrid>
            
            {(formData.lat && formData.lng) && (
              <Box p={3} bg="blue.50" borderRadius="md">
                <Text fontSize="sm" color="blue.700">
                  Coordenadas: {formData.lat?.toFixed(6)}, {formData.lng?.toFixed(6)}
                </Text>
              </Box>
            )}

            <Divider />

            {/* Información Financiera */}
            <Text fontSize="lg" fontWeight="bold" color="#5b182b">Información Financiera</Text>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
              <FormControl>
                <FormLabel>Monto Licencia</FormLabel>
                <NumberInput
                  value={formData.montoLicencia || 0}
                  onChange={(_, value) => handleInputChange('montoLicencia', value || 0)}
                  precision={2}
                  isReadOnly={isReadOnly}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
              
              <FormControl>
                <FormLabel>Fecha de Registro</FormLabel>
                <Input
                  type="date"
                  value={formData.fechaRegistro || ''}
                  onChange={(e) => handleInputChange('fechaRegistro', e.target.value)}
                  isReadOnly={isReadOnly}
                />
              </FormControl>
              
              <FormControl>
                <FormLabel>Referencia de Pago</FormLabel>
                <Input
                  value={formData.referenciaPago || ''}
                  onChange={(e) => handleInputChange('referenciaPago', e.target.value)}
                  isReadOnly={isReadOnly}
                />
              </FormControl>
              
              <FormControl>
                <FormLabel>Fecha de Pago</FormLabel>
                <Input
                  type="date"
                  value={formData.fechaPago || ''}
                  onChange={(e) => handleInputChange('fechaPago', e.target.value)}
                  isReadOnly={isReadOnly}
                />
              </FormControl>
              
              <FormControl>
                <FormLabel>Pagado</FormLabel>
                <Select
                  value={formData.pagado ? 'true' : 'false'}
                  onChange={(e) => handleInputChange('pagado', e.target.value === 'true')}
                  isDisabled={isReadOnly}
                >
                  <option value="false">No Pagado</option>
                  <option value="true">Pagado</option>
                </Select>
              </FormControl>
            </SimpleGrid>
            {modo !== 'view' && (
              <>
                <Divider />
                <Text fontSize="lg" fontWeight="bold" color="#5b182b">Documentos</Text>
                
                {/* Archivos INE existentes en modo edit */}
                {modo === 'edit' && formData.archivosINE && formData.archivosINE.length > 0 && (
                  <Box>
                    <Text fontSize="md" fontWeight="medium" mb={2}>Archivos INE actuales:</Text>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                      {formData.archivosINE.map((archivo: any, index: number) => (
                        <Box key={index} display="flex" alignItems="center" gap={2}>
                          <Button
                            size="sm"
                            variant="outline"
                            colorScheme="blue"
                            onClick={() => handleArchivoClick(archivo.url, archivo.tipo)}
                          >
                            {archivo.tipo === 'pdf' ? '📄' : '🖼️'} INE {index + 1}
                          </Button>
                          <Button
                            size="sm"
                            colorScheme="red"
                            variant="outline"
                            onClick={() => deleteArchivo(archivo.id, 'ine')}
                          >
                            ✖️
                          </Button>
                        </Box>
                      ))}
                    </SimpleGrid>
                  </Box>
                )}
                
                {/* Comprobantes existentes en modo edit */}
                {modo === 'edit' && formData.comprobantesPago && formData.comprobantesPago.length > 0 && (
                  <Box>
                    <Text fontSize="md" fontWeight="medium" mb={2}>Comprobantes actuales:</Text>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                      {formData.comprobantesPago.map((comprobante: any, index: number) => (
                        <Box key={index} display="flex" alignItems="center" gap={2}>
                          <Button
                            size="sm"
                            variant="outline"
                            colorScheme="green"
                            onClick={() => handleArchivoClick(comprobante.url, comprobante.tipo)}
                          >
                            {comprobante.tipo === 'pdf' ? '📄' : '🖼️'} {comprobante.descripcion}
                          </Button>
                          <Button
                            size="sm"
                            colorScheme="red"
                            variant="outline"
                            onClick={() => deleteArchivo(comprobante.id, 'comprobante')}
                          >
                            ✖️
                          </Button>
                        </Box>
                      ))}
                    </SimpleGrid>
                  </Box>
                )}
                
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl>
                    <FormLabel>Nuevos Archivos INE (Máximo 2)</FormLabel>
                    <Input
                      type="file"
                      multiple
                      accept="image/*,.pdf"
                      onChange={(e) => handleFileChange(e.target.files, 'ine')}
                    />
                    {archivosINE.length > 0 && (
                      <Text fontSize="sm" color="blue.600" mt={1}>
                        {archivosINE.length} archivo(s) seleccionado(s)
                      </Text>
                    )}
                  </FormControl>
                  
                  <FormControl>
                    <FormLabel>Nuevos Comprobantes de Pago (Máximo 2)</FormLabel>
                    <Input
                      type="file"
                      multiple
                      accept="image/*,.pdf"
                      onChange={(e) => handleFileChange(e.target.files, 'comprobante')}
                    />
                    {comprobantesPago.length > 0 && (
                      <Text fontSize="sm" color="blue.600" mt={1}>
                        {comprobantesPago.length} archivo(s) seleccionado(s)
                      </Text>
                    )}
                  </FormControl>
                </SimpleGrid>
              </>
            )}
            {/* Documentos */}
            {modo === 'view' && (
              <>
                <Divider />
                <Text fontSize="lg" fontWeight="bold" color="#5b182b">Documentos</Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <Box>
                    <Text fontSize="md" fontWeight="medium" mb={2}>Archivos INE</Text>
                    {formData.archivosINE && formData.archivosINE.length > 0 ? (
                      formData.archivosINE.map((archivo: any, index: number) => (
                        <Button
                          key={index}
                          size="sm"
                          variant="outline"
                          colorScheme="blue"
                          mb={2}
                          mr={2}
                          onClick={() => handleArchivoClick(archivo.url, archivo.tipo)}
                        >
                          {archivo.tipo === 'pdf' ? '📄' : '🖼️'} INE {index + 1}
                        </Button>
                      ))
                    ) : (
                      <Text fontSize="sm" color="gray.500">No hay archivos INE subidos</Text>
                    )}
                  </Box>
                  <Box>
                    <Text fontSize="md" fontWeight="medium" mb={2}>Comprobantes de Pago</Text>
                    {formData.comprobantesPago && formData.comprobantesPago.length > 0 ? (
                      formData.comprobantesPago.map((comprobante: any, index: number) => (
                        <Button
                          key={index}
                          size="sm"
                          variant="outline"
                          colorScheme="green"
                          mb={2}
                          mr={2}
                          onClick={() => handleArchivoClick(comprobante.url, comprobante.tipo)}
                        >
                          {comprobante.tipo === 'pdf' ? '📄' : '🖼️'} {comprobante.descripcion || `Comprobante ${index + 1}`}
                        </Button>
                      ))
                    ) : (
                      <Text fontSize="sm" color="gray.500">No hay comprobantes de pago subidos</Text>
                    )}
                  </Box>
                </SimpleGrid>
              </>
            )}


            <FormControl>
              <FormLabel>Descripción</FormLabel>
              <Textarea
                value={formData.descripcion || ''}
                onChange={(e) => handleInputChange('descripcion', e.target.value)}
                rows={3}
                isReadOnly={isReadOnly}
              />
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            {modo === 'view' ? 'Cerrar' : 'Cancelar'}
          </Button>
          {modo === 'view' && (
            <Button
              bg="#bb8c52"
              color="white"
              _hover={{ bg: "#a67a47" }}
              onClick={() => imprimirLicencia(formData)}
              leftIcon={<Icon as={FaPrint} />}
            >
              Imprimir
            </Button>
          )}
          {modo !== 'view' && (
            <Button
              bg="#5b182b"
              color="white"
              _hover={{ bg: "#691f30" }}
              onClick={handleSave}
              isLoading={loading}
            >
              {modo === 'create' ? 'Crear' : 'Guardar'}
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
    
    <Modal isOpen={isImageModalOpen} onClose={onImageModalClose} size="6xl">
      <ModalOverlay bg="blackAlpha.800" />
      <ModalContent bg="transparent" boxShadow="none">
        <ModalCloseButton color="white" size="lg" _hover={{ bg: 'rgba(255,255,255,0.2)' }} />
        <ModalBody p={0} display="flex" justifyContent="center" alignItems="center">
          <Image
            src={imagenSeleccionada}
            alt="Imagen ampliada"
            maxW="100%"
            maxH="90vh"
            objectFit="contain"
            borderRadius="md"
          />
        </ModalBody>
      </ModalContent>
    </Modal>
    </>
  );
};

export default ModalLicenciaVinosLicores;